/**
 * Warstwa API modułu: I-a — dokumenty zatrudnienia (pakiety, wydania scope hr).
 * Właściciel: FALA 2, agent I-a (docs/PROJEKT-00-FUNDAMENT-2026-09-02.md §5, §6).
 * Trasy backendu: api/hr-documents (bajty i ZIP: api/document-issues).
 *
 * Kontrakt: `attach(api)` dostaje instancję axios z apiService.js (interceptory JWT, cache GET, obsługa 401)
 * i zwraca OBIEKT nazwanych metod, które apiService.js doczepia do wspólnego `apiService` (jedno miejsce,
 * src/api/modules/index.js). Nazwy metod muszą być unikalne w całej aplikacji — kolizja zatrzymuje start
 * (patrz attachApiModules). Tego pliku nie dotyka nikt poza właścicielem modułu.
 *
 * ŚWIADOMIE NIE MA TU metod pobierających PLIK wydania ani ZIP pakietu: bajty wydaje WYŁĄCZNIE
 * `api/document-issues` (fundament Z-A, metody `getDocumentIssuePdf` / `getDocumentIssueBundleZip`
 * w module deptDocs). Druga droga do pliku znaczyłaby drugą bramkę i drugi dziennik dostępu.
 */
export function attach(api) {
    return {
        // ── KOGO DOTYCZY DOKUMENT ───────────────────────────────────────────────────────────────
        /** Czynni pracownicy w zasięgu wydającego ∪ teczki wstępne (te ostatnie tylko dla kadr). */
        getHrDocumentSubjects: (query) =>
            api.get("/hr-documents/subjects", { params: { query: query || undefined } }),

        /** Dane z akt do podglądu — PESEL i konto przychodzą ZAMASKOWANE, plus lista braków. */
        getHrDocumentDataPreview: (payload) => api.post("/hr-documents/data-preview", payload),

        // ── CO WYDAĆ ────────────────────────────────────────────────────────────────────────────
        /** Wzory kadrowe gotowe do wydania (aktywne, z treścią) — bez szkiców z konfiguracji. */
        getIssuableHrDocumentTemplates: () => api.get("/hr-documents/templates"),

        /** Pakiety gotowe do wydania (aktywne, z co najmniej jedną włączoną pozycją). */
        getIssuableHrDocumentBundles: () => api.get("/hr-documents/bundles/issuable"),

        // ── PODGLĄD I WYDANIE ───────────────────────────────────────────────────────────────────
        /**
         * Podgląd treści PRZED wydaniem — HTML tekstem, front wkłada go do ramki z sandboxem.
         * Nic nie zapisuje: po kliknięciu „Wydaj" dokumentu nie da się już zmienić.
         */
        previewHrDocumentHtml: (payload) =>
            api.post("/hr-documents/preview-html", payload, { responseType: "text" }),

        /** Wydaje pojedynczy dokument albo cały pakiet. Zwraca opis wydań (bajty zostają w rejestrze). */
        issueHrDocuments: (payload) => api.post("/hr-documents/issues", payload),

        /** Rejestr dokumentów zatrudnienia z filtrami i licznikami. */
        getHrDocumentIssues: (params) => api.get("/hr-documents/issues", { params }),

        /** Wydania jednej osoby — sekcja w karcie pracownika i w panelu teczki wstępnej. */
        getHrDocumentIssuesForSubject: (userId, osobaWstepnaId) =>
            api.get("/hr-documents/issues/for-subject", {
                params: { userId: userId || undefined, osobaWstepnaId: osobaWstepnaId || undefined },
            }),

        /** Unieważnia wydanie (powód obowiązkowy). Plik zostaje — egzemplarz mógł być podpisany. */
        cancelHrDocumentIssue: (id, reason) =>
            api.post(`/hr-documents/issues/${id}/cancel`, { reason }),

        // ── PODPIS I POPRAWKA (etapy 4-5) ──────────────────────────────────────────────────────
        /**
         * Wgrywa SKAN PODPISANEGO egzemplarza. Trafia do poczekalni e-teczki (a nie od razu do akt) —
         * opis i część akt zatwierdza człowiek, tak jak przy każdym innym dokumencie osobowym.
         * `Content-Type` ustawia przeglądarka razem z granicą multipart — ręczne wpisanie go psuje wysyłkę.
         */
        uploadHrDocumentSignedScan: (id, file) => {
            const dane = new FormData();
            dane.append("file", file);
            return api.post(`/hr-documents/issues/${id}/signed-scan`, dane);
        },

        /** „Wydaj ponownie": nowy dokument z pieczątką, stary zostaje widoczny jako zastąpiony. Powód obowiązkowy. */
        reissueHrDocumentIssue: (id, reason, formData) =>
            api.post(`/hr-documents/issues/${id}/reissue`, { reason, formData: formData || null }),

        /** Łańcuch wydań: pierwszy egzemplarz → obowiązujący (żeby poprawka nie chowała podpisanej wersji). */
        getHrDocumentIssueHistory: (id) => api.get(`/hr-documents/issues/${id}/history`),

        /**
         * KOMPLET PAKIETU jako jeden ZIP. Adres celowo wskazuje `api/document-issues` — bajty wydaje
         * jeden kontroler dla całej aplikacji (fundament Z-A), a bramka sprawdzana jest PER PLIK,
         * więc archiwum nigdy nie stanie się obejściem polityki pojedynczego dokumentu.
         * `responseType: "blob"` obowiązkowo: bez niego axios potraktowałby archiwum jako tekst.
         */
        getHrDocumentBundleZip: (bundleIssueId) =>
            api.get(`/document-issues/bundles/${bundleIssueId}/zip`, { responseType: "blob" }),

        // ── KONFIGURACJA PAKIETÓW (/admin/hr-config → „Pakiety dokumentów") ─────────────────────
        getHrDocumentBundles: () => api.get("/hr-documents/bundles"),
        getHrDocumentBundle: (id) => api.get(`/hr-documents/bundles/${id}`),
        createHrDocumentBundle: (dto) => api.post("/hr-documents/bundles", dto),
        updateHrDocumentBundle: (id, dto) => api.put(`/hr-documents/bundles/${id}`, dto),
        deleteHrDocumentBundle: (id) => api.delete(`/hr-documents/bundles/${id}`),
    };
}
